import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { RequireAuthContainer } from "../../components/containers/RequireAuthContainer"
import { useAuth } from "../../hooks/useAuth"
import { JobList } from "../../components/JobList"
import { JobCreateForm } from "../../components/JobCreateForm"
import apiClient from "../../api/apiClient"
import { JobRequestDTO, JobCreatePayload, Job } from "@/shared-types"
import { DashboardLayout } from "../../components/DashboardLayout"
import { useToast } from "@/hooks/use-toast"

/**
 * 📋 Dispatcher Dashboard
 * - Tabbed view: Jobs vs. Create Form
 * - Auth + Role-protected (dispatcher only)
 */
export default function DispatcherDashboard() {
  const { user, role } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [activeTab, setActiveTab] = useState<"jobs" | "create">("jobs")
  const [jobs, setJobs] = useState<Job[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<Error | null>(null)

  // 🔒 Redirect if role isn't dispatcher
  useEffect(() => {
    if (role && role !== "dispatcher") {
      navigate(`/${role}/dashboard`)
    }
  }, [role, navigate])

  // 🔃 Fetch jobs on mount
  useEffect(() => {
    if (user) fetchJobs()
  }, [user])

  // 🛰️ Load dispatcher jobs
  const fetchJobs = async () => {
    if (!user) return
    setIsLoading(true)
    setError(null)

    try {
      const { data, error } = await apiClient.getDispatcherJobs(user.id)
      if (error) throw error
      if (data) setJobs(data)
    } catch (err) {
      setError(err as Error)
      console.error("❌ Failed to fetch jobs:", err)
    } finally {
      setIsLoading(false)
    }
  }

  // 🛠 Handle job creation
  const handleCreateJob = async (data: JobCreatePayload) => {
    if (!user) return
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const payload = { ...data, created_by: user.id }
      const { data: newJob, error } = await apiClient.createJob(payload)
      if (error) throw error

      if (newJob) {
        setJobs(prev => [newJob, ...prev])
        setActiveTab("jobs")
        toast({ title: "Success", description: "Job created", variant: "success" })
      }
    } catch (err) {
      setSubmitError(err as Error)
      toast({ title: "Error", description: (err as Error).message, variant: "error" })
    } finally {
      setIsSubmitting(false)
    }
  }

  // 🚧 TODO: Assign driver modal
  const handleAssignJob = async (jobId: string) => {
    alert("🚧 TODO: Assign driver modal for job: " + jobId)
  }

  // ❌ Cancel job
  const handleCancelJob = async (jobId: string) => {
    try {
      const { error } = await apiClient.cancelJob(jobId)
      if (error) throw error

      setJobs(prev =>
        prev.map(job => (job.id === jobId ? { ...job, status: "cancelled" } : job))
      )

      toast({ title: "Job Cancelled", description: "Job cancelled", variant: "default" })
    } catch (err) {
      toast({ title: "Error", description: (err as Error).message, variant: "error" })
    }
  }

  return (
    <RequireAuthContainer>
      <DashboardLayout>
        <div className="container mx-auto px-4 py-8">
          {/* 🧭 Header */}
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Dispatcher Dashboard
            </h1>
          </div>

          {/* 📑 Tabs */}
          <div className="flex mb-6 border-b border-gray-200 dark:border-gray-700">
            {["jobs", "create"].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as "jobs" | "create")}
                className={`px-4 py-2 font-medium text-sm ${
                  activeTab === tab
                    ? "text-primary border-b-2 border-primary"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
              >
                {tab === "jobs" ? "My Jobs" : "Create New Job"}
              </button>
            ))}
          </div>

          {/* 🔄 Conditional Render */}
          {activeTab === "jobs" ? (
            <JobList
              jobs={jobs}
              isLoading={isLoading}
              error={error}
              onAssignJob={handleAssignJob}
              onCancelJob={handleCancelJob}
              emptyMessage="You haven't created any jobs yet."
            />
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <JobCreateForm
                onSubmit={handleCreateJob}
                isSubmitting={isSubmitting}
                error={submitError}
              />
            </div>
          )}
        </div>
      </DashboardLayout>
    </RequireAuthContainer>
  )
}
